import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  format, parseISO, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks,
  isWithinInterval, startOfDay, endOfDay, differenceInCalendarDays
} from 'date-fns';
import {
  FileSpreadsheet, Download, ChevronLeft, ChevronRight, Calendar,
  Copy, Check, Users, Clock, DollarSign
} from 'lucide-react';
import './GustoReport.css';

const REGULAR_HOURS_THRESHOLD = 40;

// Stripe paidAt may be ISO string, Firestore Timestamp, Date, or epoch ms.
const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    // A bare calendar date ('yyyy-MM-dd') — e.g. a manually recorded payment —
    // must be read in LOCAL time. new Date('yyyy-MM-dd') parses as UTC midnight,
    // which in western timezones lands on the previous evening and drops the tip
    // into the wrong pay period. Build it from the parts so it stays on the day
    // the owner actually entered. Full ISO timestamps (Stripe) keep their instant.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val.toDate === 'function') return val.toDate();
  if (typeof val === 'number') return new Date(val);
  return null;
};

function GustoReport() {
  const { state } = useApp();

  // Biweekly period — Week 1 starts on a Monday, Week 2 ends 13 days later (Sunday).
  // Default: the in-progress biweekly period (this Mon–Sun is Week 2, ending this Sunday).
  const [startDate, setStartDate] = useState(() => {
    const thisMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
    return subWeeks(thisMonday, 1);
  });
  const [endDate, setEndDate] = useState(() => {
    const thisMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
    return endOfWeek(thisMonday, { weekStartsOn: 1 });
  });

  // Split the selected range into Week 1 (first 7 days) and Week 2 (remainder).
  // Overtime is still computed per 7-day chunk so OT math stays correct for
  // arbitrary ranges. If the range is <= 7 days, Week 2 is empty.
  const week1Start = startDate;
  const week1End = useMemo(() => {
    const cand = addDays(startDate, 6);
    return cand > endDate ? endDate : cand;
  }, [startDate, endDate]);
  const week2Start = useMemo(() => addDays(week1End, 1), [week1End]);
  const week2End = endDate;
  const hasWeek2 = week2Start <= endDate;

  // Break the selected range into consecutive 7-day weeks from the range start.
  // Overtime has to be measured per 7-day week, so we can't treat "everything
  // after day 7" as one bucket — a range longer than two weeks (or one that
  // isn't a clean biweekly) would otherwise report phantom overtime and split
  // regular/OT hours wrong. Each window is inclusive on both ends.
  const weekWindows = useMemo(() => {
    const windows = [];
    const rangeEnd = startOfDay(endDate);
    let s = startOfDay(startDate);
    while (s <= rangeEnd) {
      const e = addDays(s, 6);
      windows.push({
        startStr: format(s, 'yyyy-MM-dd'),
        endStr: format(e > rangeEnd ? rangeEnd : e, 'yyyy-MM-dd')
      });
      s = addDays(s, 7);
    }
    return windows;
  }, [startDate, endDate]);

  const [timeRecords, setTimeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);

  // Load all time records across the selected date range
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const startStr = format(startDate, 'yyyy-MM-dd');
        const endStr = format(endDate, 'yyyy-MM-dd');

        const recordsRef = collection(db, 'timeRecords');
        const q = query(
          recordsRef,
          where('date', '>=', startStr),
          where('date', '<=', endStr)
        );
        const snap = await getDocs(q);
        setTimeRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading time records:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [startDate, endDate]);

  // Total tips per employee for invoices PAID inside this biweekly pay period.
  // Source of truth: invoice.paidAt (Stripe status_transitions.paid_at) and invoice.tipSplit.
  const tipsByEmployee = useMemo(() => {
    const periodStart = startOfDay(startDate);
    const periodEnd = endOfDay(endDate);
    const totals = {};
    let grand = 0;

    (state.invoices || []).forEach(inv => {
      if (!inv.tipAmount || inv.tipAmount <= 0) return;
      const paid = toDate(inv.paidAt);
      if (!paid) return;
      if (!isWithinInterval(paid, { start: periodStart, end: periodEnd })) return;

      // Prefer explicit per-employee split; fall back to even split across job team.
      let splits = Array.isArray(inv.tipSplit) && inv.tipSplit.length > 0
        ? inv.tipSplit.map(s => ({ employeeId: s.employeeId, amount: s.amount || 0 }))
        : null;

      if (!splits) {
        const job = inv.jobId ? state.jobs.find(j => j.id === inv.jobId) : null;
        const team = job
          ? [...new Set([...(job.team || []), ...(job.helpers || [])])]
          : [];
        if (team.length > 0) {
          const each = Math.round((inv.tipAmount / team.length) * 100) / 100;
          splits = team.map(empId => ({ employeeId: empId, amount: each }));
        }
      }

      if (!splits) return; // unassigned tip — not payable through Gusto

      splits.forEach(s => {
        if (!s.employeeId) return;
        totals[s.employeeId] = (totals[s.employeeId] || 0) + (s.amount || 0);
        grand += (s.amount || 0);
      });
    });

    return { perEmployee: totals, total: grand };
  }, [state.invoices, state.jobs, startDate, endDate]);

  // Build per-employee biweekly summary matching Gusto's columns
  const payrollRows = useMemo(() => {
    // Aggregate hours per employee into each 7-day week window (only non-rejected
    // records — treat pending and approved as payable so managers see a complete
    // picture before they finish approvals).
    const perWeek = {};

    timeRecords.forEach(rec => {
      if (!rec.employeeId || !rec.hoursWorked) return;
      if (rec.approvalStatus === 'rejected') return;

      const wi = weekWindows.findIndex(w => rec.date >= w.startStr && rec.date <= w.endStr);
      if (wi === -1) return;

      if (!perWeek[rec.employeeId]) {
        perWeek[rec.employeeId] = weekWindows.map(() => 0);
      }
      perWeek[rec.employeeId][wi] += rec.hoursWorked;
    });

    // Build a row for every employee regardless of status (active, onboarding,
    // inactive, etc.). Inactive employees can still have payable hours or tips
    // in a period — e.g. someone who worked shifts and was then deactivated —
    // so they must appear on the report. We drop the rows with nothing to pay
    // (no hours and no tips) below, after we've computed each employee's totals.
    const rows = state.employees.map(emp => {
      const weekHours = perWeek[emp.id] || weekWindows.map(() => 0);
      const rate = emp.hourlyRate || 0;

      // Overtime is calculated PER 7-day week (hours over 40 in a single week),
      // so anything beyond 40 in one week is OT even if other weeks are light.
      const reg = weekHours.map(h => Math.min(h, REGULAR_HOURS_THRESHOLD));
      const ot = weekHours.map(h => Math.max(0, h - REGULAR_HOURS_THRESHOLD));

      const w1Reg = reg[0] || 0;
      const w1OT = ot[0] || 0;
      // Fold every week beyond the first into the Week 2 columns while keeping
      // each week's overtime computed against its own 40-hour threshold.
      const w2Reg = reg.slice(1).reduce((sum, h) => sum + h, 0);
      const w2OT = ot.slice(1).reduce((sum, h) => sum + h, 0);

      const totalReg = w1Reg + w2Reg;
      const totalOT = w1OT + w2OT;
      const totalHours = totalReg + totalOT;
      const totalPay = totalReg * rate + totalOT * rate * 1.5;
      const tips = Math.round((tipsByEmployee.perEmployee[emp.id] || 0) * 100) / 100;

      return {
        id: emp.id,
        name: `${emp.lastName || ''}, ${emp.firstName || ''}`.replace(/^,\s*/, ''),
        rate,
        w1Reg, w1OT, w2Reg, w2OT,
        totalReg, totalOT, totalHours, totalPay,
        tips,
        hasHours: totalHours > 0,
        hasTips: tips > 0
      };
    });

    // Only show employees with something to pay this period — hours worked or
    // tips owed. Employees with zero hours and zero tips are excluded.
    return rows
      .filter(r => r.hasHours || r.hasTips)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [timeRecords, state.employees, tipsByEmployee, weekWindows]);

  const grandTotals = useMemo(() => {
    return payrollRows.reduce((acc, r) => ({
      w1Reg: acc.w1Reg + r.w1Reg,
      w1OT: acc.w1OT + r.w1OT,
      w2Reg: acc.w2Reg + r.w2Reg,
      w2OT: acc.w2OT + r.w2OT,
      totalHours: acc.totalHours + r.totalHours,
      totalPay: acc.totalPay + r.totalPay,
      tips: acc.tips + r.tips
    }), { w1Reg: 0, w1OT: 0, w2Reg: 0, w2OT: 0, totalHours: 0, totalPay: 0, tips: 0 });
  }, [payrollRows]);

  const fmtHrs = (h) => h > 0 ? h.toFixed(2) : '0.00';

  const copyValue = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(prev => (prev === key ? null : prev)), 1200);
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
  };

  const exportCSV = () => {
    const week2Label = hasWeek2
      ? `Week 2 Regular Hrs (${format(week2Start, 'MMM d')}-${format(week2End, 'MMM d')})`
      : 'Week 2 Regular Hrs';
    const header = [
      'Employee', 'Rate',
      `Week 1 Regular Hrs (${format(week1Start, 'MMM d')}-${format(week1End, 'MMM d')})`,
      'Week 1 Overtime Hrs',
      week2Label,
      'Week 2 Overtime Hrs',
      'Total Regular Hrs', 'Total Overtime Hrs', 'Total Hours', 'Total Pay',
      'Tips (invoices paid this period)'
    ];

    const rows = payrollRows.map(r => [
      r.name,
      r.rate ? `$${r.rate.toFixed(2)}/hr` : '',
      fmtHrs(r.w1Reg), fmtHrs(r.w1OT),
      fmtHrs(r.w2Reg), fmtHrs(r.w2OT),
      fmtHrs(r.totalReg), fmtHrs(r.totalOT),
      fmtHrs(r.totalHours),
      `$${r.totalPay.toFixed(2)}`,
      `$${r.tips.toFixed(2)}`
    ]);

    const totalRow = [
      'TOTAL', '',
      fmtHrs(grandTotals.w1Reg), fmtHrs(grandTotals.w1OT),
      fmtHrs(grandTotals.w2Reg), fmtHrs(grandTotals.w2OT),
      fmtHrs(grandTotals.w1Reg + grandTotals.w2Reg),
      fmtHrs(grandTotals.w1OT + grandTotals.w2OT),
      fmtHrs(grandTotals.totalHours),
      `$${grandTotals.totalPay.toFixed(2)}`,
      `$${grandTotals.tips.toFixed(2)}`
    ];

    const csv = [header, ...rows, totalRow]
      .map(row => row.map(cell => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gusto-payroll-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const periodLabel = `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d, yyyy')}`;
  const periodDays = differenceInCalendarDays(endDate, startDate) + 1;

  const shiftBiweekly = (direction) => {
    setStartDate(prev => {
      const next = direction > 0 ? addWeeks(prev, 2) : subWeeks(prev, 2);
      setEndDate(addDays(next, 13));
      return next;
    });
  };

  const resetToLatestBiweekly = () => {
    const thisMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
    const ws = subWeeks(thisMonday, 1);
    setStartDate(ws);
    setEndDate(endOfWeek(thisMonday, { weekStartsOn: 1 }));
  };

  const handleStartChange = (e) => {
    const d = parseISO(e.target.value);
    if (isNaN(d.getTime())) return;
    setStartDate(d);
    if (d > endDate) setEndDate(d);
  };

  const handleEndChange = (e) => {
    const d = parseISO(e.target.value);
    if (isNaN(d.getTime())) return;
    setEndDate(d);
    if (d < startDate) setStartDate(d);
  };

  return (
    <div className="gusto-report">
      <div className="report-header">
        <div>
          <h1><FileSpreadsheet size={28} /> Gusto Report</h1>
          <p>Biweekly hours formatted to match Gusto's payroll entry screen.</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={exportCSV}>
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="period-nav">
        <button
          className="nav-btn"
          onClick={() => shiftBiweekly(-1)}
          title="Previous biweekly period"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="period-display">
          <Calendar size={18} />
          <span>{periodLabel}</span>
          <span className="period-days">({periodDays} day{periodDays === 1 ? '' : 's'})</span>
        </div>
        <div className="date-range-inputs">
          <label>
            <span>From</span>
            <input
              type="date"
              value={format(startDate, 'yyyy-MM-dd')}
              onChange={handleStartChange}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={format(endDate, 'yyyy-MM-dd')}
              onChange={handleEndChange}
            />
          </label>
        </div>
        <button
          className="nav-btn"
          onClick={() => shiftBiweekly(1)}
          title="Next biweekly period"
        >
          <ChevronRight size={20} />
        </button>
        <button
          className="today-btn"
          onClick={resetToLatestBiweekly}
        >
          Latest Period
        </button>
      </div>

      <div className="gusto-tip">
        <strong>Tip:</strong> click any hours or tips value to copy it, then paste
        straight into Gusto. Overtime is per week (anything over {REGULAR_HOURS_THRESHOLD} hrs/week).
        Tips are summed from invoices whose Stripe <em>paid date</em> falls in the
        selected period. Adjust the <em>From</em> / <em>To</em> dates to recalculate.
      </div>

      {loading ? (
        <div className="loading-state">Loading payroll data...</div>
      ) : (
        <div className="gusto-table-wrap">
          <table className="gusto-table">
            <thead>
              <tr>
                <th rowSpan={2} className="col-employee">Employee</th>
                <th rowSpan={2} className="col-rate">Rate</th>
                <th colSpan={2} className="week-group week1">
                  Week 1: {format(week1Start, 'MMM d')} – {format(week1End, 'MMM d')}
                </th>
                <th colSpan={2} className="week-group week2">
                  {hasWeek2
                    ? `Week 2: ${format(week2Start, 'MMM d')} – ${format(week2End, 'MMM d')}`
                    : 'Week 2: (not in range)'}
                </th>
                <th rowSpan={2} className="col-total">Total Hrs</th>
                <th rowSpan={2} className="col-pay">Total Pay</th>
                <th rowSpan={2} className="col-tips">Tips</th>
              </tr>
              <tr>
                <th className="sub week1">Regular hrs</th>
                <th className="sub week1">Overtime hrs</th>
                <th className="sub week2">Regular hrs</th>
                <th className="sub week2">Overtime hrs</th>
              </tr>
            </thead>
            <tbody>
              {payrollRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty-row">
                    No active or onboarding employees found.
                  </td>
                </tr>
              ) : payrollRows.map(r => (
                <tr key={r.id} className={(r.hasHours || r.hasTips) ? '' : 'no-hours'}>
                  <td className="col-employee">{r.name}</td>
                  <td className="col-rate">
                    {r.rate ? `$${r.rate.toFixed(2)}/hr` : '—'}
                  </td>
                  <CopyableHourCell
                    value={r.w1Reg}
                    cellKey={`${r.id}-w1r`}
                    copiedKey={copiedKey}
                    onCopy={copyValue}
                    className="week1"
                  />
                  <CopyableHourCell
                    value={r.w1OT}
                    cellKey={`${r.id}-w1o`}
                    copiedKey={copiedKey}
                    onCopy={copyValue}
                    className="week1 ot"
                  />
                  <CopyableHourCell
                    value={r.w2Reg}
                    cellKey={`${r.id}-w2r`}
                    copiedKey={copiedKey}
                    onCopy={copyValue}
                    className="week2"
                  />
                  <CopyableHourCell
                    value={r.w2OT}
                    cellKey={`${r.id}-w2o`}
                    copiedKey={copiedKey}
                    onCopy={copyValue}
                    className="week2 ot"
                  />
                  <td className="col-total">{fmtHrs(r.totalHours)}</td>
                  <td className="col-pay">${r.totalPay.toFixed(2)}</td>
                  <CopyableMoneyCell
                    value={r.tips}
                    cellKey={`${r.id}-tips`}
                    copiedKey={copiedKey}
                    onCopy={copyValue}
                  />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="col-employee">TOTAL</td>
                <td></td>
                <td className="week1">{fmtHrs(grandTotals.w1Reg)}</td>
                <td className="week1 ot">{fmtHrs(grandTotals.w1OT)}</td>
                <td className="week2">{fmtHrs(grandTotals.w2Reg)}</td>
                <td className="week2 ot">{fmtHrs(grandTotals.w2OT)}</td>
                <td className="col-total">{fmtHrs(grandTotals.totalHours)}</td>
                <td className="col-pay">${grandTotals.totalPay.toFixed(2)}</td>
                <td className="col-tips">${grandTotals.tips.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="gusto-summary-cards">
        <div className="summary-card">
          <Users size={20} />
          <div>
            <div className="value">{payrollRows.filter(r => r.hasHours).length}</div>
            <div className="label">Employees with hours</div>
          </div>
        </div>
        <div className="summary-card">
          <Clock size={20} />
          <div>
            <div className="value">{fmtHrs(grandTotals.totalHours)}</div>
            <div className="label">Total hours in period</div>
          </div>
        </div>
        <div className="summary-card">
          <Clock size={20} />
          <div>
            <div className="value">{fmtHrs(grandTotals.w1OT + grandTotals.w2OT)}</div>
            <div className="label">Total overtime hours</div>
          </div>
        </div>
        <div className="summary-card">
          <DollarSign size={20} />
          <div>
            <div className="value">${grandTotals.tips.toFixed(2)}</div>
            <div className="label">Total tips (invoices paid this period)</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyableMoneyCell({ value, cellKey, copiedKey, onCopy }) {
  const display = `$${(value || 0).toFixed(2)}`;
  const isCopied = copiedKey === cellKey;
  const isZero = !value;
  return (
    <td className={`hour-cell tips-cell ${isZero ? 'zero' : ''}`}>
      <button
        type="button"
        className="hour-copy-btn"
        onClick={() => onCopy(cellKey, (value || 0).toFixed(2))}
        title={isZero ? 'Copy 0.00' : `Copy ${display} to clipboard`}
      >
        <span className="hour-value">{display}</span>
        <span className="copy-icon">
          {isCopied ? <Check size={12} /> : <Copy size={12} />}
        </span>
      </button>
    </td>
  );
}

function CopyableHourCell({ value, cellKey, copiedKey, onCopy, className = '' }) {
  const display = value > 0 ? value.toFixed(2) : '0.00';
  const isCopied = copiedKey === cellKey;
  const isZero = value === 0;
  return (
    <td className={`hour-cell ${className} ${isZero ? 'zero' : ''}`}>
      <button
        type="button"
        className="hour-copy-btn"
        onClick={() => onCopy(cellKey, display)}
        title={isZero ? 'Copy 0.00' : `Copy ${display} to clipboard`}
      >
        <span className="hour-value">{display}</span>
        <span className="copy-icon">
          {isCopied ? <Check size={12} /> : <Copy size={12} />}
        </span>
      </button>
    </td>
  );
}

export default GustoReport;
